"use client"

import React, { useEffect, useState } from "react"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { loadProducts } from "@/actions/productActions"
import { PlusIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { AccountContext } from "@/lib/storefront/account/account-service"
import { WishListWithId } from "@/lib/storefront/account/wishlist-service"
import { Button } from "@/components/ui/components/button"
import ImagePreviewGroup from "@/components/ui/components/image-preview-group"

interface AccountWishlistsClientProps {
  accountContext: AccountContext
  wishlists: WishListWithId[] | undefined
}

function WishlistCard({ wishlist, onRemove }: { wishlist: WishListWithId; onRemove: (key: string) => void }) {
  const t = useTranslations("storefront")
  const router = useRouter()
  // Placeholder for navigation logic
  const handleNavigate = () => {
    // TODO: Implement navigation to wishlist details
    // For now, just alert or use router.push(`/account/wishlist/${wishlist.title}`)
    alert(`Navigate to wishlist: ${wishlist.title}`)
  }
  const [products, setProducts] = useState<any[]>([])
  useEffect(() => {
    const productSkus =
      wishlist.items
        ?.map((item) => {
          const skuAttr = item.attributes?.find((attr) => attr.name === "sku")
          return skuAttr && skuAttr.value !== undefined ? String(skuAttr.value) : undefined
        })
        ?.filter(Boolean) || []
    loadProducts(productSkus as string[]).then((loadedProducts) => setProducts(loadedProducts))
  }, [wishlist])

  // Placeholder for images (WishListRO does not have images, so use a static array or empty)
  const images = products?.map((p) => p?.image) || []

  return (
    <div
      className="relative flex flex-col cursor-pointer gap-4 rounded-lg border border-gray-300 p-6 transition-transform hover:shadow-lg hover:-translate-y-1"
      onClick={handleNavigate}
    >
      {/* Header */}
      <div className="flex justify-between items-start">
        <h2 className="text-lg font-bold text-gray-900">{t("wishlist.title", { title: wishlist.title })}</h2>
        <button
          className="text-2xl text-gray-700"
          onClick={(e) => {
            e.stopPropagation()
            wishlist.id && onRemove(wishlist.id)
          }}
        >
          &times;
        </button>
      </div>

      {/* Main Content */}
      <div className="flex gap-4">
        {/* Left Images Section */}
        <div className="flex flex-col items-start gap-1">
          <ImagePreviewGroup imageUrls={images}></ImagePreviewGroup>
        </div>

        {/* Right Info Section */}
        <div className="flex-1 grid grid-cols-2 text-sm gap-x-2 gap-y-1">
          <div className="font-bold text-gray-800">{t("wishlist.name_label", { defaultValue: "Name" })}</div>
          <div>{wishlist.title}</div>

          <div className="font-bold text-gray-800">{t("wishlist.creation_date", { defaultValue: "Created" })}</div>
          <div>{wishlist.creationDate ? new Date(wishlist.creationDate).toLocaleDateString() : "-"}</div>
        </div>

        {/* Right Arrow Section */}
        <div className="flex items-center">
          <div className="flex w-[30px]">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
              <line x1="0" y1="0" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
              <line x1="0" y1="100" x2="100" y2="50" stroke="#f5f5f5" strokeWidth="10"></line>
            </svg>
          </div>
        </div>
      </div>

      {/* Divider */}
      <hr className="my-2" />

      {/* Bottom Section */}
      <div className="flex justify-between items-center">
        {/* Right Arrow to Navigate */}
        <button
          className="flex items-center gap-1 text-gray-400 hover:text-yellow-500 transition"
          onClick={handleNavigate}
        >
          <span className="text-sm">
            {wishlist.itemsCount ?? 0} {t("product.items")}
          </span>
        </button>
      </div>
    </div>
  )
}

export default function AccountWishlistsClient({ accountContext, wishlists }: AccountWishlistsClientProps) {
  const t = useTranslations("storefront")
  // Use a unique key for each wishlist (title + creationDate as fallback)
  const getKey = (w: WishListWithId) => w.id
  const [currentWishlists, setCurrentWishlists] = useState(wishlists)

  // Placeholder for remove logic
  const handleRemove = (key: string) => {
    // TODO: Implement API call to remove wishlist
    setCurrentWishlists(currentWishlists?.filter((w) => getKey(w) !== key))
  }

  return (
    <>
      <div className="flex flex-col items-center justify-center p-8 border rounded-md w-full">
        {/* Bear paw icon */}
        <div className="mb-4">
          <Image src={"/img/bear-paw.png"} alt={"bear-paw"} width={145} height={122}></Image>
        </div>

        {/* Heading and Subtext */}
        <h2 className="text-xl font-bold text-gray-800">{t("wishlist.my_wishlists")}</h2>
        <p className="text-sm text-gray-600 mb-8">
          {t("wishlist.created_wishlists", { count: currentWishlists?.length })}
        </p>
        <Button size={"lg"} variant={"outline"} className="bg-red-700 text-white">
          <PlusIcon strokeWidth={1} className="inline" />
          <span className="ml-2">{t("wishlist.create_wishlist")}</span>
        </Button>
      </div>
      <div className="flex flex-col gap-4 w-full">
        {currentWishlists?.map((wishlist) => (
          <WishlistCard key={getKey(wishlist)} wishlist={wishlist} onRemove={handleRemove} />
        ))}
      </div>
    </>
  )
}
